import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { withCurrenciesInUse } from '@norvix/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  customerFeesApi,
  ONBOARDING_FEE_NAME,
  type CustomerFee,
  type OnboardingCollectionResult,
} from '../../api/customer-fees.api.js';
import { useOfferedCurrencies } from '../../api/currencies.api.js';
import { useAuth } from '../../auth/useAuth.js';
import { APP_LOCALE } from '../../utils/locale.js';
import {
  describeOnboardingCollection,
  onboardingChipLabel,
  type CollectionMessage,
} from '../../utils/onboarding-collection.js';
import { formatTransactionAmount } from '../../utils/transaction-money.js';

/**
 * What this customer pays, and what everyone else pays.
 *
 * Two things this screen is careful about. It never shows a price without
 * saying where it came from — an override, the standard schedule, or nothing at
 * all — because "€0.35" means something different in each case. And it marks
 * the fees the platform does not actually charge yet, so setting a price here
 * is not mistaken for changing what a customer is billed.
 */

function formatCents(cents: string, currency: string): string {
  const value = Number(cents) / 100;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(value);
}

interface CustomerFeesTabProps {
  customerId: string;
  /** The client's accounts, so a fee account can be named rather than typed. */
}

export function CustomerFeesTab({ customerId }: CustomerFeesTabProps): JSX.Element {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  // Mirrors the API: only an ADMIN may take money for a fee.
  // DEV-67: pricing is changed by admin and the client manager; everyone else reads it.
  const canEdit = can('fees.edit');
  const canCollect = canEdit;
  const [currency, setCurrency] = useState('EUR');
  // DEV-162: Settings → Organisation → Currencies offered, plus any currency the client already holds.
  const offered = useOfferedCurrencies();
  const [editing, setEditing] = useState<CustomerFee | null>(null);
  const [kind, setKind] = useState<'FLAT' | 'PERCENTAGE'>('FLAT');
  const [flatAmount, setFlatAmount] = useState('0.00');
  const [basisPoints, setBasisPoints] = useState('0');
  // "0.20%, minimum 10.00" is one price, not two: a percentage alone charges
  // 10 cents on a 50.00 transfer, which does not cover making it.
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [reason, setReason] = useState('');
  /** Set when Save was pressed without a reason, so the field can say so. */
  const [reasonMissing, setReasonMissing] = useState(false);
  const [error, setError] = useState('');
  const [reverting, setReverting] = useState<CustomerFee | null>(null);
  const [revertReason, setRevertReason] = useState('');
  /** The onboarding fee row the operator asked to collect, while confirming. */
  const [confirmingCollect, setConfirmingCollect] = useState<CustomerFee | null>(null);
  /** What the last onboarding-fee collection attempt came to. */
  const [collectNotice, setCollectNotice] = useState<CollectionMessage | null>(null);

  const feesQuery = useQuery({
    queryKey: ['customer-fees', customerId, currency],
    queryFn: () => customerFeesApi.list(customerId, currency),
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['customer-fees', customerId] });
  };

  /** A reprice of the onboarding fee retries collection; say what came of it. */
  const noteCollection = (result: OnboardingCollectionResult | undefined): void => {
    setCollectNotice(result === undefined ? null : describeOnboardingCollection(result));
  };

  const collectMutation = useMutation({
    mutationFn: () => customerFeesApi.collectOnboarding(customerId, currency),
    onSuccess: async (result) => {
      setConfirmingCollect(null);
      setError('');
      noteCollection(result);
      await refresh();
    },
    onError: (err: unknown) => {
      setConfirmingCollect(null);
      setCollectNotice(null);
      setError(err instanceof Error ? err.message : 'Could not collect the onboarding fee.');
    },
  });

  const overrideMutation = useMutation({
    mutationFn: () =>
      customerFeesApi.override(customerId, {
        name: editing?.name ?? '',
        currency,
        reason: reason.trim(),
        feeType:
          kind === 'FLAT'
            ? { kind: 'FLAT', amountCents: String(Math.round(Number(flatAmount) * 100)) }
            : {
                kind: 'PERCENTAGE',
                basisPoints: Number(basisPoints),
                ...(minAmount.trim() !== ''
                  ? { minAmountCents: String(Math.round(Number(minAmount) * 100)) }
                  : {}),
                ...(maxAmount.trim() !== ''
                  ? { maxAmountCents: String(Math.round(Number(maxAmount) * 100)) }
                  : {}),
              },
      }),
    onSuccess: async (result) => {
      setEditing(null);
      setReason('');
      setError('');
      noteCollection(result.onboardingCollection);
      await refresh();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not save the override.');
    },
  });

  const revertMutation = useMutation({
    mutationFn: () =>
      customerFeesApi.revert(customerId, {
        name: reverting?.name ?? '',
        currency,
        reason: revertReason.trim(),
      }),
    onSuccess: async (result) => {
      setReverting(null);
      setRevertReason('');
      setError('');
      noteCollection(result.onboardingCollection);
      await refresh();
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : 'Could not remove the override.');
    },
  });

  /** The values startEdit would seed for this fee — the dialog's starting point. */
  function snapshotOf(fee: CustomerFee): {
    kind: 'FLAT' | 'PERCENTAGE';
    flatAmount: string;
    basisPoints: string;
    minAmount: string;
    maxAmount: string;
  } {
    const current = fee.effective?.feeType;
    if (current?.kind === 'PERCENTAGE') {
      return {
        kind: 'PERCENTAGE',
        flatAmount: '0.00',
        basisPoints: String(current.basisPoints),
        minAmount:
          current.minAmountCents !== undefined
            ? (Number(current.minAmountCents) / 100).toFixed(2)
            : '',
        maxAmount:
          current.maxAmountCents !== undefined
            ? (Number(current.maxAmountCents) / 100).toFixed(2)
            : '',
      };
    }
    if (current?.kind === 'FLAT') {
      return {
        kind: 'FLAT',
        flatAmount: (Number(current.amountCents) / 100).toFixed(2),
        basisPoints: '0',
        minAmount: '',
        maxAmount: '',
      };
    }
    return { kind: 'FLAT', flatAmount: '0.00', basisPoints: '0', minAmount: '', maxAmount: '' };
  }

  function startEdit(fee: CustomerFee): void {
    setEditing(fee);
    setError('');
    setReason('');
    setReasonMissing(false);
    const current = fee.effective?.feeType;
    setMinAmount('');
    setMaxAmount('');
    if (current?.kind === 'PERCENTAGE') {
      setKind('PERCENTAGE');
      setBasisPoints(String(current.basisPoints));
      setFlatAmount('0.00');
      if (current.minAmountCents !== undefined) {
        setMinAmount((Number(current.minAmountCents) / 100).toFixed(2));
      }
      if (current.maxAmountCents !== undefined) {
        setMaxAmount((Number(current.maxAmountCents) / 100).toFixed(2));
      }
    } else if (current?.kind === 'FLAT') {
      setKind('FLAT');
      setFlatAmount((Number(current.amountCents) / 100).toFixed(2));
      setBasisPoints('0');
    } else {
      // A tiered price cannot be edited here yet; start from a flat price
      // rather than silently reshaping the tiers.
      setKind('FLAT');
      setFlatAmount('0.00');
      setBasisPoints('0');
    }
  }

  /**
   * Has anything actually been edited?
   *
   * Compared against what the dialog opened with rather than against the stored
   * price, because a price with no override opens seeded from the standard one
   * — and saving that unchanged would create an override that changes nothing
   * while looking, ever after, like a deliberate decision.
   */
  const opened = editing === null ? null : snapshotOf(editing);
  const hasChanges =
    opened !== null &&
    (kind !== opened.kind ||
      flatAmount !== opened.flatAmount ||
      basisPoints !== opened.basisPoints ||
      minAmount !== opened.minAmount ||
      maxAmount !== opened.maxAmount);

  /**
   * Where this client's fees are taken from.
   *
   * The default is the account each transaction touched, which is right until
   * a client wants charges collected in one place. A nomination is only
   * honoured when the currency matches, so it is shown next to each account.
   */

  if (feesQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (feesQuery.isError) {
    return <Alert severity="error">Could not load this customer’s fees.</Alert>;
  }

  const data = feesQuery.data;
  const fees = data?.fees ?? [];
  const history = data?.history ?? [];
  const notChargedCount = fees.filter((f) => !f.charged).length;
  const heldCurrencies = data?.heldCurrencies ?? [];
  // Empty means we could not read their accounts, which is not the same as
  // "holds nothing" — so only warn when we actually know.
  const holdsThisCurrency = heldCurrencies.length === 0 || heldCurrencies.includes(currency);
  const onboarding = data?.onboarding ?? null;
  const onboardingLabel = onboardingChipLabel(onboarding);
  const confirmPrice = confirmingCollect?.effective?.feeType;

  return (
    <Stack spacing={3}>
      {/* The fee-source picker is gone. A commission belongs to the account
          its transaction touched; nominating another one produced a charge on
          an account with no matching money, which is what it did in production. */}

      <Stack direction="row" spacing={2} alignItems="center">
        <TextField
          select
          size="small"
          label="Currency"
          value={currency}
          onChange={(event) => setCurrency(event.target.value)}
          sx={{ minWidth: 140 }}
        >
          {withCurrenciesInUse(offered.currencies, [...heldCurrencies, currency]).map((code) => (
            <MenuItem key={code} value={code}>
              {code}
              {heldCurrencies.includes(code) ? ' — holds an account' : ''}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          A price set here applies to this customer only. Everything else falls back to the
          standard schedule.
        </Typography>
      </Stack>

      {error !== '' ? <Alert severity="error">{error}</Alert> : null}

      {collectNotice !== null ? (
        <Alert severity={collectNotice.severity} onClose={() => setCollectNotice(null)}>
          {collectNotice.text}
        </Alert>
      ) : null}

      {!holdsThisCurrency ? (
        <Alert severity="warning">
          This customer holds no {currency} account
          {heldCurrencies.length > 0 ? ` — only ${heldCurrencies.join(', ')}` : ''}. A price set
          here is stored, but fees are charged against the account being debited, so it would
          never apply.
        </Alert>
      ) : null}

      {notChargedCount > 0 ? (
        <Alert severity="info">
          {notChargedCount} of these fees are not charged by the platform yet. A price set on
          one is stored and takes effect when that path is wired up — it does not change what
          the customer is billed today.
        </Alert>
      ) : null}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Fee</TableCell>
            <TableCell>Price in force</TableCell>
            <TableCell>Source</TableCell>
            <TableCell>Standard price</TableCell>
            <TableCell align="right">On {formatCents('10000', currency)}</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {fees.map((fee) => {
            const preview = fee.effective?.preview.find((p) => p.amountCents === '10000');
            const isOnboarding = fee.name === ONBOARDING_FEE_NAME;
            return (
              <TableRow key={fee.name}>
                <TableCell>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{fee.label}</Typography>
                    {fee.charged ? null : (
                      <Tooltip title={fee.note}>
                        <Chip size="small" label="not charged yet" variant="outlined" />
                      </Tooltip>
                    )}
                    {isOnboarding && onboardingLabel !== null ? (
                      <Chip
                        size="small"
                        data-testid="onboarding-fee-status"
                        color={onboarding?.collected === true ? 'success' : 'warning'}
                        variant="outlined"
                        label={onboardingLabel}
                      />
                    ) : null}
                  </Stack>
                </TableCell>
                <TableCell>
                  {fee.effective === null ? (
                    <Typography variant="body2" color="text.secondary">
                      No price — free
                    </Typography>
                  ) : (
                    fee.effective.summary
                  )}
                </TableCell>
                <TableCell>
                  {fee.source === 'OVERRIDE' ? (
                    <Chip size="small" color="primary" label="Override" />
                  ) : fee.source === 'GLOBAL' ? (
                    <Chip size="small" label="Standard" variant="outlined" />
                  ) : (
                    <Chip size="small" label="None" variant="outlined" />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {fee.globalFeeType === null
                      ? '—'
                      : fee.globalFeeType.kind === 'FLAT'
                        ? formatCents(fee.globalFeeType.amountCents, currency)
                        : fee.globalFeeType.kind === 'PERCENTAGE'
                          ? `${(fee.globalFeeType.basisPoints / 100).toFixed(2)}%`
                          : 'tiered'}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  {preview === undefined ? '—' : formatCents(preview.feeCents, currency)}
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {isOnboarding && canCollect && onboarding !== null && !onboarding.collected ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setConfirmingCollect(fee);
                          setError('');
                        }}
                      >
                        Collect now
                      </Button>
                    ) : null}
                    {canEdit ? <Button size="small" onClick={() => startEdit(fee)}>
                      {fee.source === 'OVERRIDE' ? 'Change' : 'Override'}
                    </Button> : null}
                    {canEdit && fee.source === 'OVERRIDE' ? (
                      <Button
                        size="small"
                        color="inherit"
                        onClick={() => {
                          setReverting(fee);
                          setRevertReason('');
                          setError('');
                        }}
                      >
                        Revert
                      </Button>
                    ) : null}
                  </Stack>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Pricing history
        </Typography>
        {history.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No pricing changes have been made for this customer.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Fee</TableCell>
                <TableCell>Change</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell>{new Date(entry.at).toLocaleString(APP_LOCALE)}</TableCell>
                  <TableCell>{entry.name}</TableCell>
                  <TableCell>
                    {entry.action === 'REVERTED'
                      ? `${entry.from ?? '—'} → standard price`
                      : `${entry.from ?? 'none'} → ${entry.to ?? '—'}`}
                  </TableCell>
                  <TableCell>{entry.reason}</TableCell>
                  <TableCell>{entry.actor}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>

      <Dialog open={editing !== null} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Override “{editing?.label}” for this customer</DialogTitle>
        <DialogContent>
          {editing !== null && !editing.charged ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {editing.note}
            </Alert>
          ) : null}
          {editing?.effective?.feeType.kind === 'TIER' ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The price in force is tiered. Saving here replaces it with a single flat or
              percentage price — edit tiers on the global fee page instead.
            </Alert>
          ) : null}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Price type"
              value={kind}
              onChange={(event) => setKind(event.target.value as 'FLAT' | 'PERCENTAGE')}
            >
              <MenuItem value="FLAT">Flat amount</MenuItem>
              <MenuItem value="PERCENTAGE">Percentage of the amount</MenuItem>
            </TextField>
            {kind === 'FLAT' ? (
              <TextField
                label={`Amount (${currency})`}
                value={flatAmount}
                onChange={(event) => setFlatAmount(event.target.value)}
                helperText="Charged on every transaction of this type."
              />
            ) : (
              <TextField
                label="Basis points"
                value={basisPoints}
                onChange={(event) => setBasisPoints(event.target.value)}
                // On exactly 100.00 the fee in cents equals the basis points
                // (10000 x bps / 10000), so this hint is exact and does not
                // imply a rounding rule of its own — the row above shows the
                // server-calculated price, which is the one that binds.
                helperText={`100 bps = 1%.${
                  Number.isInteger(Number(basisPoints)) && Number(basisPoints) > 0
                    ? ` On ${formatCents('10000', currency)} that is exactly ${formatCents(
                        basisPoints,
                        currency,
                      )}.`
                    : ''
                }`}
              />
            )}
            {kind === 'PERCENTAGE' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  fullWidth
                  label={`Minimum (${currency}, optional)`}
                  value={minAmount}
                  onChange={(event) => setMinAmount(event.target.value)}
                  helperText="What they pay when the percentage comes to less."
                />
                <TextField
                  fullWidth
                  label={`Maximum (${currency}, optional)`}
                  value={maxAmount}
                  onChange={(event) => setMaxAmount(event.target.value)}
                  helperText="Caps the fee on a large transfer. Leave blank for none."
                />
              </Stack>
            ) : null}
            {kind === 'PERCENTAGE' && minAmount.trim() !== '' ? (
              <Typography variant="caption" color="text.secondary">
                {`A ${formatCents('5000', currency)} transfer would be charged ${formatCents(
                  String(
                    Math.max(
                      Math.round((5000 * Number(basisPoints)) / 10000),
                      Math.round(Number(minAmount) * 100),
                    ),
                  ),
                  currency,
                )} — the minimum, not the percentage.`}
              </Typography>
            ) : null}
            {/* The save button is disabled until this has four characters, which
                the API also enforces. Saying so only in the disabled state left
                an operator typing "fee" at a greyed-out button with no idea
                what it wanted. */}
            <TextField
              label="Reason (required)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              error={
                (reason.trim().length > 0 && reason.trim().length < 4) ||
                (reasonMissing && reason.trim().length < 4)
              }
              helperText={
                reasonMissing && reason.trim().length < 4
                  ? 'Required before this price can be saved — at least 4 characters.'
                  : reason.trim().length < 4
                    ? 'At least 4 characters — this is the audit record of why this customer’s price differs.'
                    : 'Recorded against this customer’s pricing history.'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Tooltip
            title={
              !hasChanges
                ? 'Nothing has changed yet — edit a value to save a new price.'
                : ''
            }
          >
            <span>
              <Button
                variant="contained"
                // Disabled ONLY when there is genuinely nothing to save. It used
                // to be disabled until a reason was typed as well, which read as
                // a broken button: the operator had changed the price, the
                // control did nothing, and nothing said why. A missing reason is
                // now reported when the button is pressed, where it can be read.
                disabled={!hasChanges || overrideMutation.isPending}
                onClick={() => {
                  if (reason.trim().length < 4) {
                    setReasonMissing(true);
                    return;
                  }
                  setReasonMissing(false);
                  overrideMutation.mutate();
                }}
              >
                {overrideMutation.isPending ? 'Saving…' : 'Save override'}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmingCollect !== null}
        onClose={() => setConfirmingCollect(null)}
        aria-labelledby="collect-onboarding-title"
      >
        <DialogTitle id="collect-onboarding-title">Collect the onboarding fee now?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {confirmPrice?.kind === 'FLAT'
              ? `The price in force is ${formatTransactionAmount(confirmPrice.amountCents, currency)}.`
              : `There is no flat ${currency} onboarding price in force for this client.`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            It is taken from the client’s {currency} account only if the full amount is available.
            Nothing is taken partially, and an onboarding fee is never charged twice.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmingCollect(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={collectMutation.isPending}
            onClick={() => collectMutation.mutate()}
          >
            {collectMutation.isPending ? 'Collecting…' : 'Collect fee'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={reverting !== null} onClose={() => setReverting(null)}>
        <DialogTitle>Remove the override on “{reverting?.label}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This customer goes back to the standard price. The override is kept in the history
            so past charges stay explainable.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Reason (required)"
            value={revertReason}
            onChange={(event) => setRevertReason(event.target.value)}
            sx={{ minWidth: 380 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReverting(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={revertReason.trim().length < 4 || revertMutation.isPending}
            onClick={() => revertMutation.mutate()}
          >
            {revertMutation.isPending ? 'Removing…' : 'Remove override'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
